import { Application, Container } from 'pixi.js';
import { SceneStage } from '../scene/SceneStage.js';
import { ConsolePanel, type ConsoleCommand } from '../ui/ConsolePanel.js';
import { animationForScene, detectLocale, type Ending, type Locale } from '../story/caseModel.js';
import type { StorySnapshot, StorySource } from '../story/source.js';

/**
 * Case 000, staged.
 *
 * The set (SceneStage) holds the approved lab artwork, Patch, Bit, their
 * speech bubbles and the countdown — that's where the case's questions reach
 * the player, Patch or Bit visibly "asking" them. The console (ConsolePanel)
 * is the player's own interface: the case's fictional commands, the system's
 * reaction to each one, and the final verdict. This scene only sequences the
 * beats between them — it never decides outcomes, which stay with the story
 * source (server-authoritative when configured, see story/source.ts).
 */
const REPLAY_ID = '__replay';

interface SpokenLine {
  readonly speaker: 'patch' | 'bit';
  readonly text: string;
}

const UI: Record<Locale, Record<string, string>> = {
  'en-US': {
    replay: '/replay',
    replayHint: 'Run the case again',
    solved: '=== CASE SOLVED ===',
    partial: '=== RESPONSE INCOMPLETE ===',
    incorrect: '=== WRONG CALL ===',
    booting: 'triage console online — node link unstable',
  },
  'fr-FR': {
    replay: '/rejouer',
    replayHint: "Relancer l'affaire",
    solved: '=== AFFAIRE RÉSOLUE ===',
    partial: '=== RÉPONSE INCOMPLÈTE ===',
    incorrect: '=== CONCLUSION ERRONÉE ===',
    booting: 'console de triage en ligne — lien du nœud instable',
  },
  'zh-CN': {
    replay: '/重玩',
    replayHint: '重新调查本案',
    solved: '=== 案件已解决 ===',
    partial: '=== 响应不完整 ===',
    incorrect: '=== 判断错误 ===',
    booting: '分诊控制台已上线 — 节点链路不稳定',
  },
};

export class StoryScene {
  readonly view = new Container();
  private readonly locale: Locale = detectLocale();
  private source!: StorySource;
  private snapshot!: StorySnapshot;
  private stage!: SceneStage;
  private terminal!: ConsolePanel;
  private currentSceneId = '';
  private busy = false;
  /** Bumped to cancel any speech-bubble sequence still in flight (the player
   * acted, or replayed, before Patch/Bit finished "asking"). */
  private speechSequenceId = 0;

  static async create(app: Application, source: StorySource): Promise<StoryScene> {
    const scene = new StoryScene();
    await scene.build(app, source);
    return scene;
  }

  private async build(app: Application, source: StorySource): Promise<void> {
    this.source = source;
    const { width, height } = app.screen;
    const stageHeight = Math.round(height * 0.46);

    [this.stage, this.snapshot] = await Promise.all([
      SceneStage.create(width, stageHeight),
      source.start(),
    ]);
    this.view.addChild(this.stage.view);

    this.terminal = new ConsolePanel(width, height - stageHeight);
    this.terminal.view.y = stageHeight;
    this.terminal.setOnSelect((id) => {
      void this.onSelect(id);
    });
    this.view.addChild(this.terminal.view);

    this.terminal.append(UI[this.locale]['booting'] ?? '', 'system');
    this.presentScene(true);
  }

  /** Prints a scene's header and narration, then offers its commands. */
  private presentScene(printNarration: boolean): void {
    const { scene } = this.snapshot;
    this.currentSceneId = scene.id;
    const mood = animationForScene(scene);
    this.stage.setMood(mood);
    this.stage.setEvidence(this.snapshot.evidence.count, this.snapshot.evidence.total);

    if (printNarration) {
      this.terminal.append(`== ${scene.title[this.locale].toUpperCase()} ==`, 'system');
      const isEnding = this.snapshot.isEnding;
      const paragraphs = scene.body[this.locale]
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter((text) => text.length > 0);

      const spokenLines: SpokenLine[] = [];
      paragraphs.forEach((text, index) => {
        if (isEnding) {
          // Endings are narrator/system wrap-up — nobody is "asking" anymore.
          const kind = /^[🎯🧠]/u.test(text) ? 'command' : 'narration';
          this.terminal.append(text, kind);
          return;
        }
        // Under countdown pressure, Patch voices the opening line (he wants to
        // act); Bit voices the rest everywhere (the methodical, proof-demanding
        // one). These reach the player as speech bubbles on the cast, not
        // console text — see playSpeech below.
        const speaker: 'patch' | 'bit' = mood === 'alert' && index === 0 ? 'patch' : 'bit';
        spokenLines.push({ speaker, text });
      });
      if (spokenLines.length > 0) void this.playSpeech(spokenLines);
    }
    this.offerCommands();
  }

  /** Plays Patch/Bit's lines as speech bubbles, one after another. Cancels
   * cleanly if the player acts (or replays) before it finishes — see the
   * speechSequenceId bump in runChoiceBeat/replay. */
  private async playSpeech(lines: readonly SpokenLine[]): Promise<void> {
    const sequenceId = ++this.speechSequenceId;
    for (const line of lines) {
      if (sequenceId !== this.speechSequenceId) return;
      await this.stage.speak(line.speaker, line.text);
      if (sequenceId !== this.speechSequenceId) return;
    }
    this.stage.clearSpeech();
  }

  private offerCommands(): void {
    if (this.snapshot.isEnding) {
      this.terminal.setCommands([
        {
          id: REPLAY_ID,
          text: UI[this.locale]['replay'] ?? '/replay',
          hint: UI[this.locale]['replayHint'] ?? '',
        },
      ]);
      return;
    }
    const commands: ConsoleCommand[] = this.snapshot.choices.map((choice) => {
      const command = choice.command?.[this.locale];
      const label = choice.label[this.locale];
      return command === undefined
        ? { id: choice.id, text: label }
        : { id: choice.id, text: command, hint: label };
    });
    this.terminal.setCommands(commands);
  }

  private async onSelect(id: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      if (id === REPLAY_ID) {
        await this.replay();
        return;
      }
      await this.runChoiceBeat(id);
    } catch (error) {
      console.error('[mini-app] choice failed', error);
    } finally {
      this.busy = false;
    }
  }

  /** The beat: command echoes, the characters react, the result lands. */
  private async runChoiceBeat(id: string): Promise<void> {
    const choice = this.snapshot.choices.find((candidate) => candidate.id === id);
    if (choice === undefined) return;

    // The player answered — whatever Patch/Bit were still "asking" is moot.
    // stage.setMood()/playReaction() below hide the bubble, which releases
    // any speak() a stale playSpeech() loop is still awaiting; it then sees
    // this bumped id and stops instead of queuing further lines.
    this.speechSequenceId++;
    this.terminal.setCommands([]);
    const echo = choice.command?.[this.locale] ?? choice.label[this.locale];
    this.terminal.append(echo, 'command');
    await this.terminal.whenIdle();

    const previousSceneId = this.currentSceneId;
    const { snapshot, reaction } = await this.source.choose(id);
    this.snapshot = snapshot;

    const mood = animationForScene(snapshot.scene);
    if (reaction === undefined) {
      this.stage.setMood(mood);
    } else {
      this.stage.playReaction(reaction, mood);
    }
    this.stage.setEvidence(snapshot.evidence.count, snapshot.evidence.total);

    const resultText = snapshot.reaction?.[this.locale];
    if (resultText !== undefined && resultText.length > 0) {
      this.terminal.append(resultText, reaction === 'react-negative' ? 'bad' : 'ok');
      await this.terminal.whenIdle();
    }

    if (snapshot.isEnding) {
      this.terminal.append(this.endingBanner(snapshot.scene.ending), 'system');
      this.presentScene(true);
      return;
    }
    // Staying in the same scene keeps the console session going instead of
    // reprinting the whole briefing after every investigative action.
    this.presentScene(snapshot.scene.id !== previousSceneId);
  }

  private async replay(): Promise<void> {
    this.speechSequenceId++;
    this.snapshot = await this.source.restart();
    this.stage.resetCountdown();
    this.terminal.clearLog();
    this.terminal.append(UI[this.locale]['booting'] ?? '', 'system');
    this.presentScene(true);
  }

  private endingBanner(ending: Ending): string {
    if (ending === null) return '';
    return UI[this.locale][ending] ?? '';
  }

  update(deltaMs: number): void {
    this.stage.update(deltaMs);
    this.terminal.update(deltaMs);
  }

  destroy(): void {
    this.view.destroy({ children: true });
  }
}
